'use client';

import React from 'react';
import { Lock, Sparkles } from 'lucide-react';

interface TemplateCardProps {
  template: {
    id: string;
    slug: string;
    title: string;
    svgUrl?: string | null;
    pngUrl?: string | null;
    premium: boolean;
    montessoriFocus?: string | null;
    tags: string[];
  };
  isSubscriber: boolean;
  onSelect: () => void;
  onPremiumClick?: () => void;
}

export default function TemplateCard({
  template,
  isSubscriber,
  onSelect,
  onPremiumClick,
}: TemplateCardProps) {
  const handleClick = () => {
    if (template.premium && !isSubscriber) {
      onPremiumClick?.();
    } else {
      onSelect();
    }
  };

  return (
    <div
      onClick={handleClick}
      className="group relative cursor-pointer rounded-xl border-2 border-gray-200 bg-white p-4 transition-all hover:border-green-400 hover:shadow-lg"
    >
      {/* Preview Image */}
      <div className="relative mb-3 aspect-square overflow-hidden rounded-lg bg-gray-50">
        {template.svgUrl ? (
          <img
            src={template.svgUrl}
            alt={template.title}
            className="h-full w-full object-contain p-2"
          />
        ) : template.pngUrl ? (
          <img
            src={template.pngUrl}
            alt={template.title}
            className="h-full w-full object-contain p-2"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-gray-400">
            <Sparkles size={48} />
          </div>
        )}

        {/* Overlay badges */}
        {template.premium && !isSubscriber && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <div className="rounded-full bg-white p-3 shadow-lg">
              <Lock size={32} className="text-gray-700" />
            </div>
          </div>
        )}

        {template.montessoriFocus && (
          <div className="absolute left-2 top-2 rounded-full bg-gradient-to-r from-green-500 to-lime-400 px-3 py-1 text-xs font-semibold text-white shadow-md">
            Montessori
          </div>
        )}
      </div>

      {/* Title */}
      <h3 className="mb-2 text-center font-semibold text-gray-800">
        {template.title}
      </h3>

      {/* Tags */}
      {template.tags.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1">
          {template.tags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* CTA Button */}
      <button
        className={`mt-3 w-full rounded-lg py-2 font-semibold text-white transition-colors ${
          template.premium && !isSubscriber
            ? 'bg-gray-400 hover:bg-gray-500'
            : 'bg-gradient-to-r from-green-500 to-lime-400 hover:from-green-600 hover:to-lime-500'
        }`}
      >
        {template.premium && !isSubscriber ? 'Unlock Premium' : 'Color This'}
      </button>
    </div>
  );
}

